import { useCallback, useEffect, useRef, useState } from "react";

import { pickActiveSectionId } from "./pick-active-section";
import { getAppScrollRoot } from "./scroll-to-section";

/**
 * Track which in-page section is active while scrolling the app's stage scroller (the
 * workbench body on shell v3 stage routes, `.appshell__content` on legacy routes).
 *
 * `activeId` is `undefined` when the caller passes no section ids: there is no active section to
 * name, and `T` holds no value meaning "none". That was already the runtime behaviour; the type
 * now says so instead of claiming a `T` that never existed.
 *
 * The guide-dock era offset (the sticky dock's bottom edge as the active line) retired with
 * the docks themselves; the active line is the top of the scrollport.
 */
export function useSectionScrollSpy<T extends string>(
  sectionIds: readonly T[],
): { activeId: T | undefined; setActiveId: (id: T) => void } {
  const [activeId, setActiveId] = useState<T | undefined>(() => sectionIds[0]);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const syncActiveFromScroll = useCallback(() => {
    const scrollRoot = getAppScrollRoot();
    if (scrollRoot === null) return;

    const scrollRootTop = scrollRoot.getBoundingClientRect().top;

    const atBottom =
      scrollRoot.scrollHeight - scrollRoot.scrollTop - scrollRoot.clientHeight < 48;

    if (atBottom && sectionIds.length > 0) {
      setActiveId(sectionIds[sectionIds.length - 1] as T);
      return;
    }

    const measurements = sectionIds
      .map((sectionId) => {
        const el = document.getElementById(sectionId);
        if (el === null) return null;
        return { id: sectionId, top: el.getBoundingClientRect().top };
      })
      .filter((entry): entry is { id: T; top: number } => entry !== null);

    const bestId = pickActiveSectionId(measurements, scrollRootTop);
    if (bestId !== null) {
      setActiveId(bestId);
    }
  }, [sectionIds]);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;

    const scrollRoot = getAppScrollRoot();
    const sectionElements = sectionIds
      .map((sectionId) => document.getElementById(sectionId))
      .filter((el): el is HTMLElement => el !== null);

    if (sectionElements.length === 0) return;

    observerRef.current?.disconnect();

    const observer = new IntersectionObserver(
      () => {
        syncActiveFromScroll();
      },
      {
        root: scrollRoot,
        rootMargin: "-15% 0px -50% 0px",
        threshold: [0, 0.1, 0.25, 0.5],
      },
    );

    sectionElements.forEach((el) => observer.observe(el));
    observerRef.current = observer;

    scrollRoot?.addEventListener("scroll", syncActiveFromScroll, { passive: true });
    window.addEventListener("resize", syncActiveFromScroll, { passive: true });
    syncActiveFromScroll();

    return () => {
      observer.disconnect();
      scrollRoot?.removeEventListener("scroll", syncActiveFromScroll);
      window.removeEventListener("resize", syncActiveFromScroll);
    };
  }, [sectionIds, syncActiveFromScroll]);

  return { activeId, setActiveId };
}
